import * as Sentry from '@sentry/react'

/**
 * Environment configuration for Sentry
 */
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
const IS_DEV = import.meta.env.DEV
const ENVIRONMENT = (import.meta.env.MODE || (IS_DEV ? 'development' : 'production')) as string

/**
 * Initializes Sentry error monitoring and performance tracing.
 * Sentry will ONLY initialize if VITE_SENTRY_DSN is defined in environment variables,
 * preventing unexpected errors or network noise during offline/local development without a DSN.
 */
export function initSentry(): void {
  if (!SENTRY_DSN) {
    if (IS_DEV) {
      console.info('[Sentry] VITE_SENTRY_DSN is not set. Sentry SDK initialization skipped in local dev.')
    }
    return
  }

  Sentry.init({
    // Data Source Name for Sentry project endpoint
    dsn: SENTRY_DSN,

    // Environment tag (e.g. 'development', 'production', 'staging')
    environment: ENVIRONMENT,

    // Release version tracking from env or default app version
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',

    // Browser integrations for performance tracing and user session replays
    integrations: [
      // Captures client-side page load and navigation transactions
      Sentry.browserTracingIntegration(),

      // Captures user session replay video/logs upon errors or sampled sessions
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Performance tracing sample rate (100% in development for testing; 20% in production)
    tracesSampleRate: IS_DEV ? 1.0 : 0.2,

    // Session replay sample rate for normal user sessions (10% in production)
    replaysSessionSampleRate: IS_DEV ? 0.2 : 0.1,

    // Session replay sample rate when an error occurs (100% of sessions with errors)
    replaysOnErrorSampleRate: 1.0,

    // Enable debug logging in dev mode if explicitly configured via VITE_SENTRY_DEBUG
    debug: IS_DEV && import.meta.env.VITE_SENTRY_DEBUG === 'true',

    // Privacy setting to ensure sensitive PII is sanitized by default
    sendDefaultPii: false,

    // Lifecycle hook before sending telemetry event to Sentry
    beforeSend(event) {
      if (IS_DEV) {
        console.debug('[Sentry Event Captured]:', event)
      }
      return event
    },
  })
}

// Auto-run Sentry initialization when imported
initSentry()

/**
 * Helper utility to capture exceptions with optional additional context metadata.
 * Logs to console during development while forwarding to Sentry when DSN is defined.
 */
export function captureException(error: unknown, captureContext?: Sentry.CaptureContext): string | undefined {
  if (IS_DEV) {
    console.error('[Sentry captureException]:', error, captureContext)
  }

  if (SENTRY_DSN) {
    return Sentry.captureException(error, captureContext)
  }
  return undefined
}

/**
 * Helper utility to capture informational or warning messages to Sentry.
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Sentry.CaptureContext
): string | undefined {
  if (IS_DEV) {
    console.log(`[Sentry captureMessage] [${level}]:`, message, context)
  }

  if (SENTRY_DSN) {
    return Sentry.captureMessage(message, { level, ...context })
  }
  return undefined
}

/**
 * Helper utility to set the active user context in Sentry for error correlation.
 */
export function setUser(user: Sentry.User | null): void {
  if (SENTRY_DSN) {
    Sentry.setUser(user)
  }
}

/**
 * Helper utility to clear user context upon user logout.
 */
export function clearUser(): void {
  if (SENTRY_DSN) {
    Sentry.setUser(null)
  }
}

/**
 * Helper utility to set a custom key-value tag in Sentry scope for error filtering.
 */
export function setTag(key: string, value: string): void {
  if (SENTRY_DSN) {
    Sentry.setTag(key, value)
  }
}

/**
 * Helper utility to attach structured key-value context data to Sentry events.
 */
export function setContext(name: string, context: Record<string, unknown> | null): void {
  if (SENTRY_DSN) {
    Sentry.setContext(name, context)
  }
}